import { AGING_BUCKET_TO_BE_RANGE } from './aging-balance-range';
import { formatCurrencySI } from './numbers';

const pad = (value) => String(value).padStart(2, '0');

// Reports label months "Oct 2026". Built by hand so the viewer's time zone can't move a month
// boundary, which is what going through a Date and toISOString would do.
export const monthDateRange = (month) => {
    const match = /^([A-Za-z]{3}) (\d{4})$/.exec(month || '');
    if (!match) return null;
    const index = new Date(`${match[1]} 1, 2000`).getMonth();
    if (Number.isNaN(index)) return null;
    const year = Number(match[2]);
    const lastDay = new Date(year, index + 1, 0).getDate();
    return {
        dateFrom: `${year}-${pad(index + 1)}-01`,
        dateTo: `${year}-${pad(index + 1)}-${pad(lastDay)}`,
    };
};

const withDates = (path, range, filter) => {
    const dates = range && `dateFrom=${range.dateFrom}&dateTo=${range.dateTo}`;
    const query = [filter, dates].filter(Boolean).join('&');
    return query ? `${path}?${query}` : path;
};

// Every sent invoice: the Sent tab defaults to Unpaid, where a fully paid month looks empty
const ALL_INVOICES = '/invoices/sent/all';

// Money in, matching the Payments figure. With no filter the list adds credit notes, debit notes,
// refunds and chargebacks. Every value has a checkbox in the filter panel, so it shows what's applied.
const PAYMENTS = 'filter=bank_debit,bank_transfer,card,check,wire,other_payment';

// `scope` starts with `&`, as customerScope builds it
export const paymentsFor = (scope) => `/transactions?${PAYMENTS}${scope}`;

export const invoicesForMonth = (month) => withDates(ALL_INVOICES, monthDateRange(month));
export const transactionsForMonth = (month) => withDates('/transactions', monthDateRange(month), PAYMENTS);

// From the first month to the last, for a figure that totals several months.
export const periodDateRange = (months = []) => {
    const first = monthDateRange(months[0]);
    const last = monthDateRange(months[months.length - 1]);
    return first && last ? { dateFrom: first.dateFrom, dateTo: last.dateTo } : null;
};

export const transactionsForPeriod = (months) => withDates('/transactions', periodDateRange(months), PAYMENTS);

// `asOf` is a yyyy-mm-dd date. The report can show a past date; the invoice list can't.
export const agingReport = (asOf) => `/reports/aging-balance${asOf ? `?report_date=${asOf}` : ''}`;

export const invoicesForAgingBucket = (bucket) => `/invoices/sent?invoice_state=${AGING_BUCKET_TO_BE_RANGE[bucket]}`;

// The median where the backend sends it: one very old invoice can drag an average up to hundreds
// of days. An older backend without the field still gets its average.
const hasMedian = (row, key) => Boolean(row) && Object.prototype.hasOwnProperty.call(row, key);
export const usesMedian = (data = []) => data.some((point) => hasMedian(point, 'median_days_to_pay'));

// average_payment_period_report sends 0 for a month with no payments between two real months.
// That is no data, not a zero-day average, and plotting it draws a false drop.
export const paymentPeriodPoints = (data = []) => data.map((point) => ({
    ...point,
    number_of_days: (hasMedian(point, 'median_days_to_pay') ? point.median_days_to_pay : point.number_of_days) || null,
}));

// The figure on the Days to pay tile: the latest month with payments, against the one before it.
// `change` is negative when customers paid faster.
export const latestPaymentPeriod = (points = []) => {
    const known = points.filter((point) => point.number_of_days != null);
    if (known.length === 0) return null;
    const latest = known[known.length - 1];
    const previous = known[known.length - 2];
    const result = { label: latest.display_month_string, days: Math.round(latest.number_of_days), note: null, change: null, previous: null };
    if (previous) {
        const change = result.days - Math.round(previous.number_of_days);
        const month = previous.display_month_string;
        result.change = change;
        result.previous = month;
        if (change === 0) result.note = `Same as ${month}`;
        else result.note = `${Math.abs(change)} day${Math.abs(change) === 1 ? '' : 's'} ${change < 0 ? 'faster' : 'slower'} than ${month}`;
    }
    return result;
};

// The sentence under an open chart's title, for someone who doesn't read charts for a living.
// Each one returns null when there's nothing worth saying, and the title stands alone.

export const collectionsLine = (report) => (collectionsState(report) === 'typical' && report.usual_rate > 0
    ? `You usually collect ${ratePercent(report.usual_rate)} of what customers owe each week.`
    : null);

const OVERDUE_BUCKETS = [['1', 'under 30 days'], ['31', '31 to 60 days'], ['61', '61 to 90 days'], ['91', 'over 90 days']];

// What's past due, wherever it shows: the backend's own figure where it sends one, from the same
// aging calculation, otherwise the four overdue buckets added up. One place, so the tile, the
// sentence and the donut can't disagree.
export const overdueAmount = (summary) => summary?.overdue_receivables
    ?? OVERDUE_BUCKETS.reduce((sum, [bucket]) => sum + (Number(summary?.aging_balances?.[bucket]?.amount) || 0), 0);

// `overdueTotal` is overdueAmount's figure. The buckets only say where most of it sits.
export const agingLine = (balances = {}, total, overdueTotal) => {
    if (!total) return null;
    const overdue = OVERDUE_BUCKETS.map(([bucket, words]) => ({ words, amount: Number(balances[bucket]?.amount) || 0 }));
    if (!(overdueTotal > 0)) return 'Nothing customers owe is past due.';
    const largest = overdue.reduce((top, bucket) => (bucket.amount > top.amount ? bucket : top));
    // "Most of it" only when one bucket really holds most of it
    const most = largest.amount > overdueTotal / 2 ? `, most of it ${largest.words}` : '';
    if (overdueTotal >= total) return `Everything customers owe is past due${most}.`;
    const share = overdueTotal / total;
    const lead = share < 0.01 ? 'Under 1%' : `${Math.round(share * 100)}%`;
    return `${lead} of what customers owe is past due${most}.`;
};

export const salesLine = (sold, paid, currency) => {
    const money = (amount) => formatCurrencySI(amount, currency);
    if (sold > 0 && paid > 0) {
        if (paid >= sold) return `You collected ${money(paid)}, more than the ${money(sold)} you sold.`;
        return `You collected ${money(paid)} of the ${money(sold)} you sold, about ${Math.round((100 * paid) / sold)} cents of every dollar.`;
    }
    if (paid > 0) return `You collected ${money(paid)} with no new sales.`;
    if (sold > 0) return `You sold ${money(sold)} and nothing has been paid yet.`;
    return null;
};

// `median` says the figure is the backend's median rather than an average, so it's "typically".
// The comparison is latestPaymentPeriod's own note, so the sentence and the tile say the same thing.
export const daysLine = (latest, terms, median = false) => {
    if (!latest) return null;
    const typically = median ? ' typically' : '';
    const take = terms
        ? `Customers${typically} take ${latest.days} days on Net ${terms} terms`
        : `Customers${typically} took ${latest.days} days to pay in ${latest.label}`;
    if (!latest.note) return `${take}.`;
    const note = latest.change === 0 ? `the same as ${latest.previous}` : latest.note;
    return `${take}, ${note}.`;
};

// The line under "Top customers". Shares mean little across a handful of customers, and one
// customer past 30% is the story on its own.
export const concentration = (customers, customerCount, kind) => {
    // "Top 5 are 100% of sales" when the list is every customer there is
    if (customerCount < 5 || customerCount <= customers.length) return `${customerCount} customer${customerCount === 1 ? '' : 's'}`;
    if (customers[0].share >= 0.3) return `Top customer is ${Math.round(customers[0].share * 100)}% of ${kind}`;
    const share = customers.reduce((total, customer) => total + customer.share, 0);
    return `Top ${customers.length} are ${Math.round(share * 100)}% of ${kind}`;
};

// The line above the follow up list: how much of the old balance these few customers hold.
export const followUpSummary = (customers, over60Total) => {
    const held = customers.reduce((total, customer) => total + customer.over_60, 0);
    const share = Math.round(100 * held / over60Total);
    return customers.length === 1
        ? `1 customer holds ${share}% of your 61+ day balance`
        : `${customers.length} customers hold ${share}% of your 61+ day balance`;
};

export const goneQuietSummary = (customerCount, yearlySpend) => (customerCount === 1
    ? `1 regular customer worth ${yearlySpend} a year`
    : `${customerCount} regular customers worth ${yearlySpend} a year`);

// The filter that narrows a linked list to one customer or group. customer_filter takes the
// numeric id, which is why the caller passes customer.id and not the uuid in the page's URL.
export const customerScope = (id, isGroup) => (id ? `&customer_filter=${isGroup ? 'group' : 'customer'}:${id}` : '');

export const spendNote = (insights) => {
    const { spend_trailing_12: trailing, spend_prior_12: prior } = insights || {};
    if (!prior || trailing == null) return undefined;
    const change = Math.round(100 * (trailing - prior) / prior);
    if (change === 0) return 'Level with prior 12 months';
    return `${change > 0 ? 'Up' : 'Down'} ${Math.abs(change)}% vs prior 12 months`;
};

// The customer's days to pay: the median where the backend sends it, the average before that
export const customerDaysToPay = (insights) => (
    hasMedian(insights, 'median_days_to_pay') ? insights.median_days_to_pay : insights?.average_days_to_pay
);

// Late against the same days the figure shows, so "7 days late" never sits under a median of 20
export const termsNote = (insights) => {
    const { net_terms: terms } = insights || {};
    if (terms == null) return undefined;
    const days = customerDaysToPay(insights);
    const late = hasMedian(insights, 'median_days_to_pay') ? (days != null ? days - terms : null) : insights.days_past_terms;
    return late > 0 ? `Net ${terms}, ${late} days late` : `Net ${terms}`;
};

export const percent = (share) => {
    if (share == null) return null;
    return share > 0 && share < 0.01 ? '<1%' : `${Math.round(share * 100)}%`;
};

// Never below 0: a future-dated invoice, or a date just ahead in the viewer's time zone, is today
export const daysSince = (date, today = new Date()) => Math.max(0, Math.floor((today - new Date(date)) / 86400000));

export const daysAgo = (days) => {
    if (days === 0) return 'Today';
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
};

// key, label, and the transactions filter. ACH splits in two there, because `ach` has no checkbox.
export const PAYMENT_METHODS = [
    ['ach', 'ACH', 'bank_debit,bank_transfer'],
    ['check', 'Check', 'check'],
    ['card', 'Card', 'card'],
    ['wire', 'Wire', 'wire'],
    ['other_payment', 'Other', 'other_payment'],
];

// The methods a customer actually used. Unknown keys are left out, not guessed at.
export const paymentMethods = (shares) => PAYMENT_METHODS
    .filter(([key]) => shares?.[key] > 0)
    .map(([key, label, filter]) => ({ key, label, filter, share: shares[key], color: `chart-method-${key === 'other_payment' ? 'other' : key}` }));

export const reminderStatus = ({ bounced, open_count: opens }) => {
    if (bounced) return 'Bounced';
    if (!opens) return 'Not opened';
    if (opens === 1) return 'Opened once';
    if (opens === 2) return 'Opened twice';
    return `Opened ${opens} times`;
};

export const reminderNote = (latest) => {
    if (!latest) return undefined;
    if (latest.bounced) return 'Most recent bounced';
    return latest.open_count ? 'Most recent was opened' : 'Most recent not opened';
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The customer trend in the shape the payment line draws. A month is "2026-05" here.
export const trendPoints = (trend = []) => paymentPeriodPoints(trend.map((point) => {
    const name = MONTH_NAMES[Number(point.month.slice(5, 7)) - 1];
    const days = hasMedian(point, 'median_days') ? point.median_days : point.days;
    return { month: `${name} ${point.month.slice(0, 4)}`, display_month_string: name, number_of_days: days };
}));

// A rate is a share of receivables and can pass 100%. Null is a week with no rate, not 0%.
export const ratePercent = (rate) => (rate == null ? null : `${(rate * 100).toFixed(1)}%`);

// By hand on UTC, so the viewer's time zone can't move a week. A week is a "2026-10-26" Monday.
const addDays = (date, days) => new Date(Date.parse(`${date}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);

export const weekLabel = (date) => `${MONTH_NAMES[Number(date.slice(5, 7)) - 1]} ${Number(date.slice(8, 10))}`;

export const weekRange = (weekStart) => ({ dateFrom: weekStart, dateTo: addDays(weekStart, 6) });

export const transactionsForWeek = (weekStart, filter = PAYMENTS) => withDates('/transactions', weekRange(weekStart), filter);

// An estimate of $0 says nothing to expect, so it reads as no estimate at all
const hasEstimate = (report) => report?.estimate?.expected > 0;

// Something is owed but nothing came in for 13 weeks, so there's no rate to estimate from. An
// older backend sent a $0 estimate for the same thing.
export const RECENT_WEEKS = 13;

export const collectionsState = (report) => {
    if (!report?.weeks_available) return 'empty';
    if (hasEstimate(report)) return 'typical';
    const reason = report.estimate_unavailable_reason;
    if (reason === 'not_enough_weeks') return 'early';
    if (reason === 'no_recent_payments' || (report.estimate && !reason)) return 'no_recent_payments';
    return 'no_receivables';
};

// The number Collections leads with, wherever it shows: this week's estimate when there is one,
// otherwise last week. Last week is the latest by date, as the chart draws it, not the last row.
export const collectionsHeadline = (report) => {
    const money = (amount) => formatCurrencySI(amount || 0, report.currency);
    if (collectionsState(report) === 'typical') {
        const { expected, low, high } = report.estimate;
        // A range that doesn't hold the estimate ($133 expected, "Likely $0 to $0") says nothing
        const range = low < high && low <= expected && expected <= high;
        return { label: 'Expected this week', value: money(expected), note: range ? `Likely ${money(low)} to ${money(high)}` : undefined };
    }
    const weeks = collectionsWeeks(report);
    const lastWeek = weeks[weeks.length - 1];
    return { label: 'Collected last week', value: money(lastWeek?.payments), note: collectionsNote(report), lastWeek };
};

// The line under "Collected last week" when there's no estimate, or nothing to say
export const collectionsNote = (report) => {
    const state = collectionsState(report);
    // Short enough for a dashboard tile
    if (state === 'empty') return `Estimate after ${report?.min_weeks_for_estimate} weeks`;
    if (state === 'early') {
        const left = report.min_weeks_for_estimate - report.weeks_available;
        return `Estimate in ${left} week${left === 1 ? '' : 's'}`;
    }
    if (state === 'no_recent_payments') return 'No recent payments';
    return undefined;
};

// Oldest first. The API sends a row for every week of the year, so the ones before the merchant's
// first payment are marked `stub`: placeholders with nothing to read or link.
export const collectionsWeeks = (report) => {
    const rows = [...(report?.data || [])].sort((a, b) => a.week_start.localeCompare(b.week_start));
    const firstReal = rows.length - (report?.weeks_available || 0);
    return rows.map((row, index) => ({ ...row, stub: index < firstReal }));
};

// A bar can reach this many times the median week before it is clipped. One week with almost no
// opening receivables can have a rate of 400, which would flatten every other bar to a sliver.
const OUTLIER_MULTIPLE = 3;
// The usual line sits no higher than this share of the chart, leaving room for its label above it
const USUAL_LINE_HEIGHT = 0.8;

const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

// What the bars draw, and `top`, the value at the top of the chart. The scale comes from the data
// because rates vary by merchant and can pass 1. A bar above the outlier cap is drawn at the top
// and marked `clipped`, keeping its real rate for the tooltip. A week with no rate gets a low
// placeholder rather than a zero-height bar.
export const collectionsBars = (report) => {
    const weeks = collectionsWeeks(report);
    const { estimate, open_receivables: open, usual_rate: usual } = report || {};
    const last = weeks[weeks.length - 1];
    const estimateRate = hasEstimate(report) && open > 0 && last ? estimate.expected / open : null;
    const rates = [...weeks.map((week) => week.rate), estimateRate].filter((rate) => rate > 0);
    const highest = rates.length > 0 ? Math.min(Math.max(...rates), median(rates) * OUTLIER_MULTIPLE) : 0;
    const top = Math.max(highest, (usual || 0) / USUAL_LINE_HEIGHT) || 1;
    const draw = (rate) => ({ value: rate == null ? top * 0.04 : Math.min(rate, top), clipped: rate > top });
    const bars = weeks.map((week) => ({ ...week, ...draw(week.rate) }));
    if (estimateRate != null) {
        bars.push({
            week_start: addDays(last.week_start, 7),
            isEstimate: true,
            ...draw(estimateRate),
            rate: estimateRate,
            payments: estimate.expected,
            opening_receivables: open,
        });
    }
    return { bars, top };
};

// The last few weeks of a report, for a chart too narrow to draw a year of bars
export const lastWeeks = (report, count) => {
    if (!report?.data || report.data.length <= count) return report;
    const data = [...report.data].sort((a, b) => a.week_start.localeCompare(b.week_start)).slice(-count);
    return { ...report, data, weeks_available: Math.min(report.weeks_available || 0, count) };
};

// The first week of each month, for a few months of weeks where quarters would leave one label
export const monthTicks = (bars) => bars
    .filter((bar, index) => index > 0 && bar.week_start.slice(5, 7) !== bars[index - 1].week_start.slice(5, 7))
    .map((bar) => bar.week_start);

// One label a quarter, at the first week of Jan, Apr, Jul and Oct, so a phone can fit them
export const quarterTicks = (bars) => bars
    .filter((bar, index) => {
        const month = Number(bar.week_start.slice(5, 7));
        return index > 0 && month !== Number(bars[index - 1].week_start.slice(5, 7)) && month % 3 === 1;
    })
    .map((bar) => bar.week_start);

export const monthName = (date) => MONTH_NAMES[Number(date.slice(5, 7)) - 1];
